import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import {
  Settings,
  Search,
  X,
  User,
  Mail,
  Phone,
  Building2,
  DollarSign,
  Tag,
  Users,
  ChevronDown,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../ui/command';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import { cn } from '../../lib/utils';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { value: 'open', label: 'Open' },
  { value: 'won', label: 'Won' },
  { value: 'lost', label: 'Lost' },
  { value: 'review', label: 'Review' },
];

const SOURCE_OPTIONS = [
  { value: 'manual', label: 'Manual' },
  { value: 'whop', label: 'Whop' },
  { value: 'website', label: 'Website' },
  { value: 'referral', label: 'Referral' },
  { value: 'free_training', label: 'Free Training' },
  { value: 'self_repair', label: 'Self Repair' },
  { value: 'automation', label: 'Automation' },
];

const AddOpportunityModal = ({
  open,
  onOpenChange,
  token,
  agencyId = null,
  isAgency = false,
  pipelines = [],
  defaultPipelineId = null,
  defaultStageId = null,
  onSuccess,
}) => {
  // Form state
  const [formData, setFormData] = useState({
    contact_id: null,
    contact_name: '',
    email: '',
    phone: '',
    title: '', // Opportunity Name
    pipeline_id: '',
    stage_id: '',
    status: 'open',
    value: '',
    owner_id: '',
    followers: [],
    business_name: '',
    source: 'manual',
    tags: [],
  });
  
  // UI state
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [contactSearchOpen, setContactSearchOpen] = useState(false);
  const [contactSearchQuery, setContactSearchQuery] = useState('');
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [availableTags, setAvailableTags] = useState([]);
  const [tagsLoading, setTagsLoading] = useState(false);
  const [tagSearchOpen, setTagSearchOpen] = useState(false);
  const [followerSearchOpen, setFollowerSearchOpen] = useState(false);
  
  // Get stages for selected pipeline
  const stages = useMemo(() => {
    const pipeline = pipelines.find(p => p.id === formData.pipeline_id);
    return pipeline?.stages || [];
  }, [pipelines, formData.pipeline_id]);
  
  // Headers for API calls
  const headers = useMemo(() => {
    const h = { Authorization: `Bearer ${token}` };
    if (isAgency && agencyId) {
      h['X-Agency-ID'] = agencyId;
    }
    return h;
  }, [token, isAgency, agencyId]);
  
  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setFormData({
        contact_id: null,
        contact_name: '',
        email: '',
        phone: '',
        title: '',
        pipeline_id: defaultPipelineId || (pipelines[0]?.id || ''),
        stage_id: defaultStageId || '',
        status: 'open',
        value: '',
        owner_id: '',
        followers: [],
        business_name: '',
        source: 'manual',
        tags: [],
      });
      setError(null);
      fetchUsers();
      fetchTags();
    }
  }, [open, defaultPipelineId, defaultStageId, pipelines]);
  
  // Set default stage when pipeline changes
  useEffect(() => {
    if (formData.pipeline_id && stages.length > 0 && !formData.stage_id) {
      setFormData(prev => ({ ...prev, stage_id: stages[0].id }));
    }
  }, [formData.pipeline_id, stages]);
  
  // Fetch users for owner/followers
  const fetchUsers = async () => {
    setUsersLoading(true);
    try {
      const endpoint = isAgency 
        ? `${API}/agency/contacts/filters/users`
        : `${API}/platform/contacts/filters/users`;
      const res = await axios.get(endpoint, { headers });
      setUsers(res.data.options || []);
    } catch (err) {
      console.error('Failed to fetch users:', err);
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  };
  
  // Fetch tags
  const fetchTags = async () => {
    setTagsLoading(true);
    try {
      const endpoint = isAgency 
        ? `${API}/agency/contacts/filters/tags`
        : `${API}/platform/contacts/filters/tags`;
      const res = await axios.get(endpoint, { headers });
      setAvailableTags(res.data.options || []);
    } catch (err) {
      console.error('Failed to fetch tags:', err);
      setAvailableTags([]);
    } finally {
      setTagsLoading(false);
    }
  };
  
  // Search contacts
  const searchContacts = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setContacts([]);
      return;
    }
    
    setContactsLoading(true);
    try {
      const endpoint = isAgency 
        ? `${API}/agency/contacts?search=${encodeURIComponent(query)}&limit=20`
        : `${API}/platform/contacts?search=${encodeURIComponent(query)}&limit=20`;
      const res = await axios.get(endpoint, { headers });
      setContacts(res.data.contacts || []);
    } catch (err) {
      console.error('Failed to search contacts:', err);
      setContacts([]);
    } finally {
      setContactsLoading(false);
    }
  }, [headers, isAgency]);
  
  // Debounced contact search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (contactSearchQuery) {
        searchContacts(contactSearchQuery);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [contactSearchQuery, searchContacts]);
  
  // Handle contact selection
  const handleSelectContact = (contact) => {
    setFormData(prev => ({
      ...prev,
      contact_id: contact.id,
      contact_name: contact.full_name || `${contact.first_name || ''} ${contact.last_name || ''}`.trim(),
      email: contact.email || prev.email,
      phone: contact.phone || prev.phone,
      business_name: contact.company_name || prev.business_name,
    }));
    setContactSearchOpen(false);
  };
  
  // Handle tag toggle
  const handleToggleTag = (tagValue) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.includes(tagValue)
        ? prev.tags.filter(t => t !== tagValue)
        : [...prev.tags, tagValue]
    }));
  };
  
  // Handle follower toggle
  const handleToggleFollower = (userId) => {
    setFormData(prev => ({
      ...prev,
      followers: prev.followers.includes(userId)
        ? prev.followers.filter(f => f !== userId)
        : [...prev.followers, userId]
    }));
  };
  
  // Clear selected contact
  const handleClearContact = () => {
    setFormData(prev => ({
      ...prev,
      contact_id: null,
      contact_name: '',
      email: '',
      phone: '',
    }));
  };
  
  // Update field
  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (field === 'pipeline_id') {
      setFormData(prev => ({ ...prev, stage_id: '' }));
    }
  };
  
  // Submit form
  const handleSubmit = async () => {
    // Validation
    if (!formData.contact_name.trim()) {
      setError('Primary Contact Name is required');
      return;
    }
    if (!formData.title.trim()) {
      setError('Opportunity Name is required');
      return;
    }
    if (!formData.pipeline_id) {
      setError('Please select a Pipeline');
      return;
    }
    if (!formData.stage_id) {
      setError('Please select a Stage');
      return;
    }
    
    setSubmitting(true);
    setError(null);
    
    try {
      const endpoint = isAgency 
        ? `${API}/agency/opportunities`
        : `${API}/platform/opportunities`;
      
      const payload = {
        ...formData,
        value: parseFloat(formData.value) || 0,
        agency_id: isAgency ? undefined : (agencyId || 'default_agency'),
      };
      
      const res = await axios.post(endpoint, payload, { headers });
      
      if (res.data.opportunity) {
        onSuccess?.(res.data.opportunity);
        onOpenChange(false);
      }
    } catch (err) {
      console.error('Failed to create opportunity:', err);
      setError(err.response?.data?.detail || 'Failed to create opportunity');
    } finally {
      setSubmitting(false);
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-hidden flex flex-col" data-testid="add-opportunity-modal">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>Add new opportunity</DialogTitle>
          <DialogDescription>
            Create new opportunity by filling in details and selecting a contact
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex-1 overflow-y-auto min-h-0">
          <div className="flex gap-6">
            {/* Left Rail */}
            <div className="w-44 flex-shrink-0 border-r border-slate-200 pr-4 flex flex-col">
              <div className="space-y-2">
                <div className="px-3 py-2 bg-emerald-50 text-emerald-700 rounded-md text-sm font-medium border-l-2 border-emerald-600">
                  Opportunity Details
                </div>
              </div>
              
              <div className="mt-auto pt-40">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button 
                        className="flex items-center gap-2 text-sm text-emerald-600 hover:text-emerald-700 opacity-50 cursor-not-allowed"
                        disabled
                        data-testid="add-manage-fields-link"
                      >
                        <Settings className="h-4 w-4" />
                        Add/Manage Fields
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Manage Fields coming in Phase 2</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>
            
            {/* Main Form */}
            <div className="flex-1 space-y-6 pr-2">
              {/* Error Message */}
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
                  {error}
                </div>
              )}
              
              {/* Contact Details Section */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-stone-900">Contact details</h3>
                
                {/* Primary Contact Name - Searchable */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-1">
                    Primary Contact Name <span className="text-red-500">*</span>
                  </Label>
                  <Popover open={contactSearchOpen} onOpenChange={setContactSearchOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={contactSearchOpen}
                        className="w-full justify-between h-10 font-normal"
                        data-testid="contact-search-trigger"
                      >
                        {formData.contact_name || 'Select Contact'}
                        {formData.contact_id ? (
                          <X 
                            className="h-4 w-4 text-slate-400 hover:text-slate-600" 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleClearContact();
                            }}
                          />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-slate-400" />
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[400px] p-0" align="start">
                      <Command shouldFilter={false}>
                        <CommandInput
                          placeholder="Search contacts..."
                          value={contactSearchQuery}
                          onValueChange={setContactSearchQuery}
                          data-testid="contact-search-input"
                        />
                        <CommandList className="max-h-[300px] overflow-y-auto">
                          {contactsLoading && (
                            <div className="p-4 text-center text-sm text-slate-500">
                              Searching...
                            </div>
                          )}
                          {!contactsLoading && contactSearchQuery.length >= 2 && contacts.length === 0 && (
                            <CommandEmpty>
                              <div className="p-4 text-center">
                                <p className="text-sm text-slate-500">No contacts found</p>
                                <p className="text-xs text-slate-400 mt-1">
                                  Enter contact name manually below
                                </p>
                              </div>
                            </CommandEmpty>
                          )}
                          {!contactsLoading && contactSearchQuery.length < 2 && (
                            <div className="p-4 text-center text-sm text-slate-500">
                              Type at least 2 characters to search
                            </div>
                          )}
                          <CommandGroup>
                            {contacts.map((contact) => (
                              <CommandItem
                                key={contact.id}
                                value={contact.id}
                                onSelect={() => handleSelectContact(contact)}
                                className="cursor-pointer"
                              >
                                <div className="flex items-center gap-3 w-full">
                                  <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 text-sm font-medium">
                                    {(contact.first_name?.[0] || contact.email?.[0] || 'C').toUpperCase()}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium truncate">
                                      {contact.full_name || `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || 'Unknown'}
                                    </p>
                                    <p className="text-xs text-slate-500 truncate">
                                      {contact.email || 'No email'}
                                    </p>
                                  </div>
                                </div>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                      
                      {/* Manual entry option */}
                      <div className="border-t p-3">
                        <Label className="text-xs text-slate-500">Or enter name manually:</Label>
                        <Input
                          placeholder="Enter contact name"
                          value={formData.contact_id ? '' : formData.contact_name}
                          onChange={(e) => {
                            setFormData(prev => ({
                              ...prev,
                              contact_id: null,
                              contact_name: e.target.value,
                            }));
                          }}
                          className="mt-1"
                          data-testid="contact-name-manual"
                        />
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
                
                {/* Primary Email & Phone Row */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Primary Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => updateField('email', e.target.value)}
                        placeholder="Enter Email"
                        className="pl-9"
                        data-testid="opp-email"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Primary Phone</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => updateField('phone', e.target.value)}
                        placeholder="Phone"
                        className="pl-9"
                        data-testid="opp-phone"
                      />
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Opportunity Details Section */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-stone-900">Opportunity Details</h3>
                
                {/* Opportunity Name */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-1">
                    Opportunity Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => updateField('title', e.target.value)}
                    placeholder="Enter opportunity name"
                    data-testid="opp-name"
                  />
                </div>
                
                {/* Pipeline & Stage Row */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Pipeline</Label>
                    <Select 
                      value={formData.pipeline_id} 
                      onValueChange={(v) => updateField('pipeline_id', v)}
                    >
                      <SelectTrigger data-testid="opp-pipeline-select">
                        <SelectValue placeholder="Select Pipeline" />
                      </SelectTrigger>
                      <SelectContent>
                        {pipelines.map((pipeline) => (
                          <SelectItem key={pipeline.id} value={pipeline.id}>
                            {pipeline.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Stage</Label>
                    <Select 
                      value={formData.stage_id} 
                      onValueChange={(v) => updateField('stage_id', v)}
                      disabled={!formData.pipeline_id}
                    >
                      <SelectTrigger data-testid="opp-stage-select">
                        <SelectValue placeholder="Select Stage" />
                      </SelectTrigger>
                      <SelectContent>
                        {stages.map((stage) => (
                          <SelectItem key={stage.id} value={stage.id}>
                            <div className="flex items-center gap-2">
                              {stage.color && (
                                <span 
                                  className="w-2 h-2 rounded-full flex-shrink-0" 
                                  style={{ backgroundColor: stage.color }} 
                                />
                              )}
                              {stage.name}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {/* Status & Value Row */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Status</Label>
                    <Select 
                      value={formData.status} 
                      onValueChange={(v) => updateField('status', v)}
                    >
                      <SelectTrigger data-testid="opp-status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUS_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Opportunity Value</Label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        type="number"
                        value={formData.value}
                        onChange={(e) => updateField('value', e.target.value)}
                        placeholder="0"
                        className="pl-9"
                        data-testid="opp-value"
                      />
                    </div>
                  </div>
                </div>
                
                {/* Owner & Followers Row */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Owner</Label>
                    <Select 
                      value={formData.owner_id} 
                      onValueChange={(v) => updateField('owner_id', v)}
                      disabled={usersLoading || users.length === 0}
                    >
                      <SelectTrigger data-testid="opp-owner-select">
                        <User className="h-4 w-4 mr-2 text-slate-400" />
                        <SelectValue placeholder={users.length === 0 ? 'No users available' : 'Unassigned'} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unassigned">Unassigned</SelectItem>
                        {users.map((user) => (
                          <SelectItem key={user.value} value={user.value}>
                            {user.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Followers</Label>
                    <Popover open={followerSearchOpen} onOpenChange={setFollowerSearchOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-between h-10 font-normal"
                          disabled={usersLoading || users.length === 0}
                          data-testid="opp-followers-trigger"
                        >
                          <div className="flex items-center gap-2">
                            <Users className="h-4 w-4 text-slate-400" />
                            {formData.followers.length > 0 
                              ? `${formData.followers.length} selected`
                              : users.length === 0 ? 'No users available' : 'Add Followers'
                            }
                          </div>
                          <ChevronDown className="h-4 w-4 text-slate-400" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[250px] p-0" align="start">
                        <Command>
                          <CommandInput placeholder="Search users..." />
                          <CommandList className="max-h-[200px] overflow-y-auto">
                            <CommandEmpty>No users found</CommandEmpty>
                            <CommandGroup>
                              {users.map((user) => (
                                <CommandItem
                                  key={user.value}
                                  value={user.value}
                                  onSelect={() => handleToggleFollower(user.value)}
                                  className="cursor-pointer"
                                >
                                  <div className="flex items-center gap-2 w-full">
                                    <div className={cn(
                                      'w-4 h-4 border rounded flex items-center justify-center',
                                      formData.followers.includes(user.value) 
                                        ? 'bg-emerald-600 border-emerald-600 text-white'
                                        : 'border-slate-300'
                                    )}>
                                      {formData.followers.includes(user.value) && (
                                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                      )}
                                    </div>
                                    <span className="text-sm">{user.label}</span>
                                  </div>
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
                
                {/* Business Name & Source Row */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Business Name</Label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        value={formData.business_name}
                        onChange={(e) => updateField('business_name', e.target.value)}
                        placeholder="Enter Business Name"
                        className="pl-9"
                        data-testid="opp-business-name"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Opportunity Source</Label>
                    <Select 
                      value={formData.source} 
                      onValueChange={(v) => updateField('source', v)}
                    >
                      <SelectTrigger data-testid="opp-source-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {SOURCE_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {/* Tags */}
                <div className="space-y-2">
                  <Label>Tags</Label>
                  <Popover open={tagSearchOpen} onOpenChange={setTagSearchOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-between h-10 font-normal"
                        disabled={tagsLoading}
                        data-testid="opp-tags-trigger"
                      >
                        <div className="flex items-center gap-2 flex-wrap overflow-hidden">
                          <Tag className="h-4 w-4 text-slate-400 flex-shrink-0" />
                          {formData.tags.length > 0 ? (
                            <div className="flex gap-1 flex-wrap">
                              {formData.tags.slice(0, 3).map(tag => (
                                <Badge key={tag} variant="secondary" className="text-xs">
                                  {tag}
                                </Badge>
                              ))}
                              {formData.tags.length > 3 && (
                                <span className="text-xs text-slate-500">+{formData.tags.length - 3} more</span>
                              )}
                            </div>
                          ) : (
                            <span className="text-slate-500">Add tags</span>
                          )}
                        </div>
                        <ChevronDown className="h-4 w-4 text-slate-400 flex-shrink-0" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[300px] p-0" align="start">
                      <Command>
                        <CommandInput placeholder="Search tags..." />
                        <CommandList className="max-h-[200px] overflow-y-auto">
                          <CommandEmpty>No tags found</CommandEmpty>
                          <CommandGroup>
                            {availableTags.map((tag) => (
                              <CommandItem
                                key={tag.value}
                                value={tag.value}
                                onSelect={() => handleToggleTag(tag.value)}
                                className="cursor-pointer"
                              >
                                <div className="flex items-center gap-2 w-full">
                                  <div className={cn(
                                    'w-4 h-4 border rounded flex items-center justify-center',
                                    formData.tags.includes(tag.value) 
                                      ? 'bg-emerald-600 border-emerald-600 text-white'
                                      : 'border-slate-300'
                                  )}>
                                    {formData.tags.includes(tag.value) && (
                                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                      </svg>
                                    )}
                                  </div>
                                  <span className="text-sm">{tag.label}</span>
                                </div>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <DialogFooter className="flex-shrink-0 border-t pt-4 mt-4">
          <Button 
            variant="outline" 
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit}
            disabled={submitting || !formData.contact_name || !formData.title}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="create-opportunity-btn"
          >
            {submitting ? 'Creating...' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddOpportunityModal;
